import React, { useState, useRef } from 'react';
import {
    Box,
    TextField,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    useMediaQuery,
    useTheme,
    Typography,
    Chip,
    Tooltip,
} from '@mui/material';
import {
    Send,
    AttachFile,
    EmojiEmotions,
    InsertPhoto,
    InsertDriveFile,
    LocationOn,
    Mic,
    Close,
    VideoLibrary,
    PlayArrow,
    Pause,
    Reply,
} from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { IRoom, IMessage } from '../../../redux/messages/types';
import {
    UserProjectSearchDialogue,
    TMediaType,
} from '../../../components/UserProjectSearchDialogue/UserProjectSearchDialogue';
import { MapModal } from '../../../components/MapModal/MapModal';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { MessageVideoThumbnail } from './MessageVideoThumbnail';
import { IProject, ISound, IUserSummary } from '../../../types/global/helper';
import { showToastMessage } from '../../../utils/AppUtils';

// Define types for our attachments
export interface IMediaAttachment {
    type: 'image' | 'document';
    files: File[];
    previews: string[];
}

export interface IProjectAttachment {
    type: 'video' | 'audio';
    ids: string[];
    titles: string[];
    thumbnailUrls?: string[];
    projects: (IProject | ISound)[]; // Store the entire project or sound objects
}

export interface ILocationAttachment {
    type: 'location';
    addressDescription: string;
    coord: string[];
    imageUrl: string;
}

// Union type for all attachment types
export type AttachmentType = IMediaAttachment | IProjectAttachment | ILocationAttachment;

interface MessageInputProps {
    message: string;
    onMessageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSendMessage: () => void;
    onKeyPress: (e: React.KeyboardEvent) => void;
    room?: IRoom;
    onEmojiClick: (emoji: string) => void;
    attachments?: AttachmentType[];
    onAttachmentsChange?: (attachments: AttachmentType[]) => void;
    replyingTo?: IMessage | null;
    onCancelReply?: () => void;
    getUserInfo?: (userId: string | IUserSummary) => IUserSummary;
}

export const MessageInput: React.FC<MessageInputProps> = ({
    message,
    onMessageChange,
    onSendMessage,
    onKeyPress,
    onEmojiClick,
    attachments: externalAttachments,
    onAttachmentsChange,
    replyingTo,
    onCancelReply,
    getUserInfo,
}) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    // Refs for file inputs
    const imageInputRef = useRef<HTMLInputElement>(null);
    const documentInputRef = useRef<HTMLInputElement>(null);

    // State for attachment menu
    const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState<null | HTMLElement>(null);
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

    // State for attachments - use external if provided, otherwise use internal state
    const [internalAttachments, setInternalAttachments] = useState<AttachmentType[]>([]);

    // Determine which attachments to use
    const attachments = externalAttachments !== undefined ? externalAttachments : internalAttachments;

    // State for project search dialogs
    const [projectSearchOpen, setProjectSearchOpen] = useState(false);
    const [projectSearchType, setProjectSearchType] = useState<TMediaType>('video');

    // State for map modal
    const [mapModalOpen, setMapModalOpen] = useState(false);

    // State for audio playback
    const [playingAudio, setPlayingAudio] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Add file size constraints
    const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3MB
    const MAX_DOCUMENT_SIZE = 1 * 1024 * 1024; // 1MB

    // Update attachments state with appropriate handler
    const updateAttachments = (newAttachments: AttachmentType[]) => {
        if (onAttachmentsChange) {
            onAttachmentsChange(newAttachments);
        } else {
            setInternalAttachments(newAttachments);
        }
    };

    // Helper to check if we already have an attachment
    const hasAttachment = () => {
        return attachments.length > 0;
    };

    // Handle attachment menu
    const handleAttachmentClick = (event: React.MouseEvent<HTMLElement>) => {
        // If we already have an attachment, show a warning or replace
        if (hasAttachment()) {
            // Here you could show a warning to the user
            // For now, let's just allow them to replace the current attachment
            setAttachmentMenuAnchor(event.currentTarget);
        } else {
            setAttachmentMenuAnchor(event.currentTarget);
        }
    };

    const handleAttachmentMenuClose = () => {
        setAttachmentMenuAnchor(null);
    };

    // Handle image attachment
    const handleImageAttachment = () => {
        // Only clear if we have a different type of attachment
        if (hasAttachment() && attachments[0].type !== 'image') {
            updateAttachments([]);
        }

        imageInputRef.current?.click();
        handleAttachmentMenuClose();
    };

    // Handle document attachment
    const handleDocumentAttachment = () => {
        // Only clear if we have a different type of attachment
        if (hasAttachment() && attachments[0].type !== 'document') {
            updateAttachments([]);
        }

        documentInputRef.current?.click();
        handleAttachmentMenuClose();
    };

    // Handle video attachment
    const handleVideoAttachment = () => {
        // Only clear if we have a different type of attachment
        if (hasAttachment() && attachments[0].type !== 'video') {
            updateAttachments([]);
        }

        setProjectSearchType('video');
        setProjectSearchOpen(true);
        handleAttachmentMenuClose();
    };

    // Handle audio attachment
    const handleAudioAttachment = () => {
        // Only clear if we have a different type of attachment
        if (hasAttachment() && attachments[0].type !== 'audio') {
            updateAttachments([]);
        }

        setProjectSearchType('audio');
        setProjectSearchOpen(true);
        handleAttachmentMenuClose();
    };

    // Handle location attachment
    const handleLocationAttachment = () => {
        // Clear any existing attachments before adding a new one
        if (hasAttachment()) {
            updateAttachments([]);
        }

        setMapModalOpen(true);
        handleAttachmentMenuClose();
    };

    // Handle file selection with size validation
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'document') => {
        const files = e.target.files;
        if (files && files.length > 0) {
            // Convert FileList to array and limit to 20 files
            const fileArray = Array.from(files).slice(0, 20);

            // Check file sizes
            const maxSize = type === 'image' ? MAX_IMAGE_SIZE : MAX_DOCUMENT_SIZE;
            const sizeLimit = type === 'image' ? '3MB' : '1MB';

            // Filter out files that are too large
            const validFiles = fileArray.filter(file => {
                if (file.size > maxSize) {
                    showToastMessage(
                        `File "${file.name}" exceeds the ${sizeLimit} limit and will be skipped.`,
                        'warning',
                    );
                    return false;
                }
                return true;
            });

            // If no valid files remain, show error and return
            if (validFiles.length === 0) {
                showToastMessage(`All selected files exceed the ${sizeLimit} size limit.`, 'error');
                e.target.value = '';
                return;
            }

            // Show compression info for images over 1MB
            if (type === 'image') {
                const filesToCompress = validFiles.filter(file => file.size > 1024 * 1024);
                if (filesToCompress.length > 0) {
                    showToastMessage(
                        `${filesToCompress.length} image(s) will be compressed to optimize message delivery.`,
                        'info',
                    );
                }
            }

            // Create previews for image files
            const previews =
                type === 'image' ? validFiles.map(file => URL.createObjectURL(file)) : validFiles.map(() => '');

            // If we already have an attachment of this type, add to it instead of replacing
            if (hasAttachment() && attachments[0].type === type) {
                const existingAttachment = attachments[0] as IMediaAttachment;

                // Combine existing files with new ones (up to 20 total)
                const combinedFiles = [...existingAttachment.files, ...validFiles].slice(0, 20);

                // Combine existing previews with new ones
                const combinedPreviews = [...existingAttachment.previews, ...previews].slice(0, 20);

                const updatedAttachment: IMediaAttachment = {
                    type,
                    files: combinedFiles,
                    previews: combinedPreviews,
                };

                updateAttachments([updatedAttachment]);
            } else {
                // Create a new attachment
                const attachment: IMediaAttachment = {
                    type,
                    files: validFiles,
                    previews,
                };

                // Replace any existing attachment with the new one
                updateAttachments([attachment]);
            }
        }
        // Reset the input value so the same file can be selected again
        e.target.value = '';
    };

    // Handle project selection
    const handleProjectSelected = (selectedProjects: (IProject | ISound)[]) => {
        if (selectedProjects.length > 0) {
            const projects = selectedProjects;

            // Convert TMediaType to the specific types supported by IProjectAttachment
            const attachmentType = projectSearchType === 'video' ? 'video' : 'audio';

            // Extract all needed info from projects
            const ids = projects.map(p => p._id);
            const titles = projects.map(p => p.title);

            // Handle thumbnails based on project type
            const thumbnailUrls = projects.map(p => {
                if ('sound_category' in p) {
                    return p.thumbnail; // For sound/audio
                }
                return p.feature_image || p.thumbnail || ''; // For video
            });

            const newAttachment: IProjectAttachment = {
                type: attachmentType,
                ids,
                titles,
                thumbnailUrls,
                projects, // Store all project objects
            };

            // Replace any existing attachment with the new one
            updateAttachments([newAttachment]);
        }
    };

    // Handle location selection
    const handleLocationSelected = (location: { addressDescription: string; coord: string[]; imageUrl: string }) => {
        const newAttachment: ILocationAttachment = {
            type: 'location',
            addressDescription: location.addressDescription,
            coord: location.coord,
            imageUrl: location.imageUrl,
        };

        // Replace any existing attachment with the new one
        updateAttachments([newAttachment]);
        setMapModalOpen(false);
    };

    // Remove attachment
    const handleRemoveAttachment = (index: number) => {
        const newAttachments = [...attachments];
        newAttachments.splice(index, 1);
        updateAttachments(newAttachments);
    };

    // Function to handle audio playback
    const handleAudioPlayback = (audioId: string, audioUrl?: string) => {
        if (!audioUrl) return;

        if (playingAudio === audioId) {
            // If this audio is already playing, pause it
            if (audioRef.current) {
                audioRef.current.pause();
            }
            setPlayingAudio(null);
        } else {
            // If another audio is playing, stop it
            if (audioRef.current) {
                audioRef.current.pause();
            }

            // Play the new audio
            const audio = new Audio(audioUrl);
            audio.onended = () => setPlayingAudio(null);
            audioRef.current = audio;
            audio.play();
            setPlayingAudio(audioId);
        }
    };

    // Render attachment preview
    const renderAttachmentPreview = (attachment: AttachmentType, index: number) => {
        switch (attachment.type) {
            case 'image': {
                const imageAttachment = attachment as IMediaAttachment;
                return (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            maxWidth: '100%',
                            overflowX: 'auto',
                        }}>
                        {imageAttachment.previews.map((preview, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    position: 'relative',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    width: 80,
                                    height: 80,
                                }}>
                                <img
                                    src={preview}
                                    alt={`attachment ${idx + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        // Remove just this file from the attachment
                                        const newPreviews = [...imageAttachment.previews];
                                        const newFiles = [...imageAttachment.files];
                                        newPreviews.splice(idx, 1);
                                        newFiles.splice(idx, 1);

                                        if (newPreviews.length === 0) {
                                            // If no files left, remove the entire attachment
                                            handleRemoveAttachment(index);
                                        } else {
                                            // Otherwise update with remaining files
                                            const updatedAttachment: IMediaAttachment = {
                                                ...imageAttachment,
                                                previews: newPreviews,
                                                files: newFiles,
                                            };
                                            const updatedAttachments = [...attachments];
                                            updatedAttachments[index] = updatedAttachment;
                                            updateAttachments(updatedAttachments);
                                        }
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        p: 0.5,
                                        '&:hover': {
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                        },
                                    }}>
                                    <Close fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                );
            }
            case 'document': {
                const docAttachment = attachment as IMediaAttachment;
                return (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            maxWidth: '100%',
                            overflowX: 'auto',
                        }}>
                        {docAttachment.files.map((file, idx) => (
                            <Chip
                                key={idx}
                                label={file.name}
                                onDelete={() => {
                                    // Remove just this file from the attachment
                                    const newPreviews = [...docAttachment.previews];
                                    const newFiles = [...docAttachment.files];
                                    newPreviews.splice(idx, 1);
                                    newFiles.splice(idx, 1);

                                    if (newFiles.length === 0) {
                                        // If no files left, remove the entire attachment
                                        handleRemoveAttachment(index);
                                    } else {
                                        // Otherwise update with remaining files
                                        const updatedAttachment: IMediaAttachment = {
                                            ...docAttachment,
                                            previews: newPreviews,
                                            files: newFiles,
                                        };
                                        const updatedAttachments = [...attachments];
                                        updatedAttachments[index] = updatedAttachment;
                                        updateAttachments(updatedAttachments);
                                    }
                                }}
                                icon={<InsertDriveFile />}
                                sx={{
                                    bgcolor: theme[themeStyle].deep_grey,
                                    color: theme[themeStyle].tag_white,
                                    '& .MuiChip-deleteIcon': {
                                        color: theme[themeStyle].grey,
                                        '&:hover': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                );
            }
            case 'video': {
                const videoAttachment = attachment as IProjectAttachment;
                return (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2, // Increased gap between video thumbnails
                            maxWidth: '100%',
                            overflowX: 'auto',
                            pb: 1, // Add some bottom padding
                        }}>
                        {videoAttachment.projects.map((project, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    width: 120,
                                    height: 80,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)', // Add a subtle shadow
                                    position: 'relative', // Add position relative for absolute positioning of delete button
                                }}>
                                <MessageVideoThumbnail
                                    title={videoAttachment.titles[idx]}
                                    thumbnailUrl={videoAttachment.thumbnailUrls?.[idx]}
                                    projectId={videoAttachment.ids[idx]}
                                    project={project as IProject}
                                    onDelete={() => {
                                        // Remove just this project from the attachment
                                        const newIds = [...videoAttachment.ids];
                                        const newTitles = [...videoAttachment.titles];
                                        const newThumbnails = videoAttachment.thumbnailUrls
                                            ? [...videoAttachment.thumbnailUrls]
                                            : [];
                                        const newProjects = [...videoAttachment.projects];

                                        newIds.splice(idx, 1);
                                        newTitles.splice(idx, 1);
                                        if (newThumbnails.length > idx) {
                                            newThumbnails.splice(idx, 1);
                                        }
                                        newProjects.splice(idx, 1);

                                        if (newProjects.length === 0) {
                                            // If no projects left, remove the entire attachment
                                            handleRemoveAttachment(index);
                                        } else {
                                            // Otherwise update with remaining projects
                                            const updatedAttachment: IProjectAttachment = {
                                                ...videoAttachment,
                                                ids: newIds,
                                                titles: newTitles,
                                                thumbnailUrls: newThumbnails.length > 0 ? newThumbnails : undefined,
                                                projects: newProjects,
                                            };
                                            const updatedAttachments = [...attachments];
                                            updatedAttachments[index] = updatedAttachment;
                                            updateAttachments(updatedAttachments);
                                        }
                                    }}
                                />
                                {/* Add delete button overlay */}
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        // Remove just this project from the attachment
                                        const newIds = [...videoAttachment.ids];
                                        const newTitles = [...videoAttachment.titles];
                                        const newThumbnails = videoAttachment.thumbnailUrls
                                            ? [...videoAttachment.thumbnailUrls]
                                            : [];
                                        const newProjects = [...videoAttachment.projects];

                                        newIds.splice(idx, 1);
                                        newTitles.splice(idx, 1);
                                        if (newThumbnails.length > idx) {
                                            newThumbnails.splice(idx, 1);
                                        }
                                        newProjects.splice(idx, 1);

                                        if (newProjects.length === 0) {
                                            // If no projects left, remove the entire attachment
                                            handleRemoveAttachment(index);
                                        } else {
                                            // Otherwise update with remaining projects
                                            const updatedAttachment: IProjectAttachment = {
                                                ...videoAttachment,
                                                ids: newIds,
                                                titles: newTitles,
                                                thumbnailUrls: newThumbnails.length > 0 ? newThumbnails : undefined,
                                                projects: newProjects,
                                            };
                                            const updatedAttachments = [...attachments];
                                            updatedAttachments[index] = updatedAttachment;
                                            updateAttachments(updatedAttachments);
                                        }
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        p: 0.5,
                                        zIndex: 5, // Ensure it's above the video thumbnail
                                        '&:hover': {
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                        },
                                    }}>
                                    <Close fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                );
            }
            case 'audio': {
                const audioAttachment = attachment as IProjectAttachment;
                return (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            maxWidth: '100%',
                            overflowX: 'auto',
                        }}>
                        {audioAttachment.projects.map((sound, idx) => {
                            // Get audio URL based on object type
                            let audioUrl = '';
                            if ('sound_category' in sound) {
                                // It's an ISound
                                audioUrl = (sound as ISound).sound;
                            } else {
                                // It's an IProject
                                audioUrl = (sound as IProject).project;
                            }

                            const audioId = `${sound._id}-${idx}`;
                            const isPlaying = playingAudio === audioId;

                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 1,
                                    }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleAudioPlayback(audioId, audioUrl)}
                                        sx={{
                                            mr: 0.5,
                                            bgcolor: theme[themeStyle].deep_grey,
                                            color: isPlaying
                                                ? theme[themeStyle].highlight
                                                : theme[themeStyle].tag_white,
                                            '&:hover': {
                                                bgcolor: theme[themeStyle].secondaryBg,
                                            },
                                        }}>
                                        {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                                    </IconButton>
                                    <Chip
                                        label={audioAttachment.titles[idx]}
                                        onDelete={() => {
                                            // If currently playing, stop it
                                            if (playingAudio === audioId && audioRef.current) {
                                                audioRef.current.pause();
                                                setPlayingAudio(null);
                                            }

                                            // Remove just this sound from the attachment
                                            const newIds = [...audioAttachment.ids];
                                            const newTitles = [...audioAttachment.titles];
                                            const newProjects = [...audioAttachment.projects];

                                            newIds.splice(idx, 1);
                                            newTitles.splice(idx, 1);
                                            newProjects.splice(idx, 1);

                                            if (newProjects.length === 0) {
                                                // If no sounds left, remove the entire attachment
                                                handleRemoveAttachment(index);
                                            } else {
                                                // Otherwise update with remaining sounds
                                                const updatedAttachment: IProjectAttachment = {
                                                    ...audioAttachment,
                                                    ids: newIds,
                                                    titles: newTitles,
                                                    projects: newProjects,
                                                };
                                                const updatedAttachments = [...attachments];
                                                updatedAttachments[index] = updatedAttachment;
                                                updateAttachments(updatedAttachments);
                                            }
                                        }}
                                        icon={<Mic />}
                                        sx={{
                                            bgcolor: theme[themeStyle].deep_grey,
                                            color: theme[themeStyle].tag_white,
                                            '& .MuiChip-deleteIcon': {
                                                color: theme[themeStyle].grey,
                                                '&:hover': {
                                                    color: theme[themeStyle].tag_white,
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                );
            }
            case 'location':
                const locAttachment = attachment as ILocationAttachment;
                return (
                    <Box
                        key={index}
                        sx={{
                            position: 'relative',
                            borderRadius: 1,
                            overflow: 'hidden',
                            width: 150,
                            height: 80,
                            mr: 1,
                        }}>
                        <img
                            src={locAttachment.imageUrl}
                            alt="location"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                p: 0.5,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'white',
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '110px',
                                }}>
                                {locAttachment.addressDescription}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveAttachment(index)}
                                sx={{
                                    color: 'white',
                                    p: 0.2,
                                }}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    // Clean up audio on unmount
    React.useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, []);

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '80px', // Fixed height for the input area
                borderTop: `1px solid ${theme[themeStyle].grey}`,
                bgcolor: theme[themeStyle].secondaryBg,
                zIndex: 10,
            }}>
            {/* Attachment preview area with optional reply info - positioned absolutely above the input */}
            {(attachments.length > 0 || replyingTo) && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '80px', // Position it above the input area
                        left: 0,
                        right: 0,
                        padding: 2,
                        paddingBottom: 1,
                        borderTop: `1px solid ${theme[themeStyle].grey}`,
                        bgcolor: theme[themeStyle].secondaryBg,
                        zIndex: 10,
                    }}>
                    {/* Reply information section */}
                    {replyingTo && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: attachments.length > 0 ? 2 : 0,
                                pb: attachments.length > 0 ? 1 : 0,
                                borderBottom: attachments.length > 0 ? `1px solid ${theme[themeStyle].grey}30` : 'none',
                            }}>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                <Reply fontSize="small" sx={{ color: theme[themeStyle].highlight, mr: 1 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme[themeStyle].highlight,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            display: 'block',
                                        }}>
                                        Replying to{' '}
                                        {getUserInfo && typeof replyingTo.created_by === 'string'
                                            ? getUserInfo(replyingTo.created_by).firstName
                                            : typeof replyingTo.created_by !== 'string'
                                            ? replyingTo.created_by.firstName
                                            : 'User'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme[themeStyle].grey,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {replyingTo.message_content && replyingTo.message_content.length > 50
                                            ? `${replyingTo.message_content.substring(0, 50)}...`
                                            : replyingTo.message_content}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton size="small" onClick={onCancelReply} sx={{ color: theme[themeStyle].grey }}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Box>
                    )}

                    {/* Attachment section */}
                    {attachments.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}>
                            {/* Attachment type label */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme[themeStyle].grey,
                                        textTransform: 'uppercase',
                                        fontSize: '0.7rem',
                                    }}>
                                    {attachments[0].type === 'image' && 'Photo'}
                                    {attachments[0].type === 'document' && 'Document'}
                                    {attachments[0].type === 'video' && 'Video'}
                                    {attachments[0].type === 'audio' && 'Audio'}
                                    {attachments[0].type === 'location' && 'Location'}
                                    {' Attachment'}
                                </Typography>

                                {/* Clear attachment button */}
                                <IconButton
                                    onClick={() => updateAttachments([])}
                                    size="small"
                                    sx={{
                                        color: theme[themeStyle].grey,
                                        p: 0.5,
                                    }}>
                                    <Close fontSize="small" />
                                </IconButton>
                            </Box>

                            {/* Attachment preview */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    width: '100%',
                                    position: 'relative',
                                }}>
                                {renderAttachmentPreview(attachments[0], 0)}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* Input area - fixed at the bottom */}
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', p: 2 }}>
                <IconButton
                    onClick={handleAttachmentClick}
                    sx={{
                        color: hasAttachment() ? theme[themeStyle].highlight : theme[themeStyle].tag_white,
                        position: 'relative',
                    }}>
                    <AttachFile />
                    {hasAttachment() && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: theme[themeStyle].highlight,
                            }}
                        />
                    )}
                </IconButton>

                <TextField
                    fullWidth
                    placeholder={isMobile ? 'Type...' : 'Type a message...'}
                    value={message}
                    onChange={onMessageChange}
                    onKeyDown={onKeyPress}
                    variant="outlined"
                    multiline
                    maxRows={4}
                    sx={{
                        mx: 1,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: theme[themeStyle].deep_grey,
                            color: theme[themeStyle].tag_white,
                            borderRadius: 2,
                            '& fieldset': {
                                borderColor: 'transparent',
                            },
                            '&:hover fieldset': {
                                borderColor: 'transparent',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme[themeStyle].highlight,
                            },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: theme[themeStyle].grey,
                            opacity: 1,
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <IconButton
                                onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
                                sx={{ color: theme[themeStyle].tag_white }}>
                                <EmojiEmotions />
                            </IconButton>
                        ),
                    }}
                />

                <IconButton
                    onClick={onSendMessage}
                    disabled={!message.trim() && attachments.length === 0}
                    sx={{
                        color:
                            message.trim() || attachments.length > 0
                                ? theme[themeStyle].highlight
                                : theme[themeStyle].grey,
                    }}>
                    <Send />
                </IconButton>
            </Box>

            {/* Emoji picker - position it relative to the fixed input */}
            {emojiPickerOpen && (
                <Paper
                    sx={{
                        position: 'absolute',
                        bottom: '80px', // Position above the input area
                        right: '20px',
                        p: 2,
                        bgcolor: theme[themeStyle].deep_grey,
                        boxShadow: 3,
                        zIndex: 12, // Higher z-index than attachments
                    }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {['😀', '😂', '❤️', '👍', '🎉', '🔥', '😎', '🤔'].map(emoji => (
                            <Box
                                key={emoji}
                                onClick={() => onEmojiClick(emoji)}
                                sx={{
                                    p: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: theme[themeStyle].secondaryBg },
                                    borderRadius: 1,
                                }}>
                                {emoji}
                            </Box>
                        ))}
                    </Box>
                </Paper>
            )}

            {/* Attachment menu */}
            <Menu
                anchorEl={attachmentMenuAnchor}
                open={Boolean(attachmentMenuAnchor)}
                onClose={handleAttachmentMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].secondaryBg,
                        color: theme[themeStyle].tag_white,
                        overflow: 'visible',
                        mt: -1,
                    },
                }}>
                <Typography
                    sx={{
                        p: 1,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: '12px',
                        color: theme[themeStyle].grey,
                        textAlign: 'center',
                        borderBottom: `1px solid ${theme[themeStyle].grey}30`,
                    }}>
                    File Size Limits: Images 3MB, Documents 1MB
                </Typography>
                <MenuItem onClick={handleImageAttachment} sx={{ fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                    <Tooltip title="Images up to 3MB. Larger images will be compressed.">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <InsertPhoto sx={{ mr: 1, color: theme[themeStyle].highlight }} />
                            Photos
                        </Box>
                    </Tooltip>
                </MenuItem>
                <MenuItem onClick={handleDocumentAttachment} sx={{ fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                    <Tooltip title="Documents up to 1MB">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <InsertDriveFile sx={{ mr: 1, color: theme[themeStyle].highlight }} />
                            Documents
                        </Box>
                    </Tooltip>
                </MenuItem>
                <MenuItem onClick={handleVideoAttachment} sx={{ fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <VideoLibrary sx={{ mr: 1, color: theme[themeStyle].highlight }} />
                        Videos
                    </Box>
                </MenuItem>
                <MenuItem onClick={handleAudioAttachment} sx={{ fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Mic sx={{ mr: 1, color: theme[themeStyle].highlight }} />
                        Audio
                    </Box>
                </MenuItem>
                <MenuItem onClick={handleLocationAttachment} sx={{ fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOn sx={{ mr: 1, color: theme[themeStyle].highlight }} />
                        Location
                    </Box>
                </MenuItem>
            </Menu>

            {/* Project Search Dialog for video and audio */}
            <UserProjectSearchDialogue
                open={projectSearchOpen}
                onClose={() => setProjectSearchOpen(false)}
                type={projectSearchType}
                maxSelection={20}
                onSubmit={handleProjectSelected}
            />

            {/* Map Modal for location */}
            <MapModal
                isOpen={mapModalOpen}
                onClose={() => setMapModalOpen(false)}
                onSelectClick={handleLocationSelected}
            />

            {/* Hidden file inputs */}
            <input
                type="file"
                ref={imageInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                multiple
                onChange={e => handleFileChange(e, 'image')}
            />

            <input
                type="file"
                ref={documentInputRef}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.txt"
                multiple
                onChange={e => handleFileChange(e, 'document')}
            />
        </Box>
    );
};
